import _ from 'lodash';
import { mapUser } from '../API/UserApi';
import { BookChapter } from './BookChapter';
import { User } from './User';
import { UserArticle, UserArticleProgressState } from './UserArticle';

export enum BookStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export class Book {
  id: string;
  status: BookStatus;
  title: string;
  description: string;
  coverImagePath?: string;
  coverVideoPath?: string;
  contentVideoPath?: string;
  coverImageTriggeredAt?: Date;
  coverImageGeneratedAt?: Date;
  coverVideoTriggeredAt?: Date;
  coverVideoGeneratedAt?: Date;
  contentVideoTriggeredAt?: Date;
  contentVideoGeneratedAt?: Date;
  numberOfChapters: number;
  lastChapterNumber: number;
  outline: string | null;
  universeId?: string;
  createdByUserId?: string;
  createdBy?: User;
  coAuthorUserId?: string;
  coAuthor?: User;
  abandonedAt?: Date;
  chapters?: BookChapter[];
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  deletedAt?: Date;
  likes?: number;

  constructor(data: any = {}) {
    // First copy all base properties
    Object.assign(this, data);

    // Transform dates exactly as in mapBook
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.publishedAt = data.publishedAt
      ? new Date(data.publishedAt)
      : undefined;
    this.deletedAt = data.deletedAt ? new Date(data.deletedAt) : undefined;
    this.coverImageTriggeredAt = data.coverImageTriggeredAt
      ? new Date(data.coverImageTriggeredAt)
      : undefined;
    this.coverImageGeneratedAt = data.coverImageGeneratedAt
      ? new Date(data.coverImageGeneratedAt)
      : undefined;
    this.coverVideoTriggeredAt = data.coverVideoTriggeredAt
      ? new Date(data.coverVideoTriggeredAt)
      : undefined;
    this.coverVideoGeneratedAt = data.coverVideoGeneratedAt
      ? new Date(data.coverVideoGeneratedAt)
      : undefined;
    this.contentVideoTriggeredAt = data.contentVideoTriggeredAt
      ? new Date(data.contentVideoTriggeredAt)
      : undefined;
    this.contentVideoGeneratedAt = data.contentVideoGeneratedAt
      ? new Date(data.contentVideoGeneratedAt)
      : undefined;
    this.abandonedAt = data.abandonedAt
      ? new Date(data.abandonedAt)
      : undefined;

    this.createdBy = data.createdBy ? mapUser(data.createdBy) : undefined;
    this.coAuthor = data.coAuthor ? mapUser(data.coAuthor) : undefined;

    this.chapters = undefined;
    this.chapters = data.chapters?.map((chapter: any) => {
      const bookChapter = new BookChapter(chapter);
      bookChapter.book = _.cloneDeep(this);
      return bookChapter;
    });
  }

  /**
   * Checks if the user is either the creator or co-author of the book
   */
  isAmongBookAuthors(userId: string): boolean {
    if (!userId) {
      return false;
    }

    return this.createdByUserId === userId || this.coAuthorUserId === userId;
  }

  canEdit(userId: string): boolean {
    return (
      this.isAmongBookAuthors(userId) &&
      this.isCompleted() &&
      this.status === BookStatus.PUBLISHED
    );
  }

  canPublish(userId: string): boolean {
    return (
      this.isAmongBookAuthors(userId) &&
      this.isCompleted() &&
      this.status === BookStatus.DRAFT
    );
  }

  canRead(userId: string): boolean {
    return this.getFirstChapter().canReadChapter(userId) ?? false;
  }

  isCompleted(): boolean {
    return (
      this.lastChapterNumber === this.numberOfChapters &&
      this.chapters?.every((chapter) => chapter.isGenerated())
    );
  }

  getFirstChapter(): BookChapter | undefined {
    return this.getChapter(1);
  }

  getChapter(chapterNumber: number): BookChapter | undefined {
    return this.chapters.find((c) => c.chapterNumber === chapterNumber);
  }

  /**
   * Gets the last chapter of the book
   */
  getLastChapter({
    onlyGenerated = true,
  }: {
    onlyGenerated?: boolean;
  } = {}): BookChapter | undefined {
    if (!this.chapters || this.chapters.length === 0) {
      return undefined;
    }

    return [...this.chapters]
      .filter((c) => !onlyGenerated || c.article?.generatedAt)
      .sort((a, b) => b.chapterNumber - a.chapterNumber)
      .find((c) => c.article);
  }

  /**
   * Checks if the book is in collaborative mode (has a co-author)
   */
  isCollaborative(): boolean {
    return !!this.coAuthorUserId;
  }

  /**
   * Checks if a co-author has abandoned the book
   */
  isAbandoned(): boolean {
    return !!this.abandonedAt;
  }

  /**
   * Gets all authors (creator and co-author) of the book
   */
  getAuthors(): User[] {
    const authors: User[] = [];

    if (this.createdByUserId) {
      if (!this.createdBy) {
        throw new Error('createdBy reference is not loaded');
      }
      authors.push(this.createdBy);
    }

    if (this.coAuthorUserId) {
      if (!this.coAuthor) {
        throw new Error('coAuthor reference is not loaded');
      }
      authors.push(this.coAuthor);
    }

    return authors;
  }

  getMyCoAuthor(userId: string): User | undefined {
    if (!this.isAmongBookAuthors(userId)) {
      return undefined;
    }

    if (!this.isCollaborative()) {
      return undefined;
    }

    return this.getAuthors().find((a) => a.id !== userId);
  }

  getMyLastChapter(
    userId: string,
    userArticles: UserArticle[],
  ): BookChapter | undefined {
    return this.chapters
      ?.sort((a, b) => b.chapterNumber - a.chapterNumber)
      .find((c) => {
        const userArticle = userArticles.find(
          (ua) => ua.article.id === c.article?.id,
        );
        return userArticle?.state !== UserArticleProgressState.COMPLETED;
      });
  }
}
